import { useEffect, useRef, useState } from 'react';
import { Disc3, Pause, Play, SkipBack, SkipForward, Volume2, VolumeX, Shuffle, Repeat, Repeat1, ExternalLink, Loader2, ListMusic, RotateCcw } from 'lucide-react';
import { tracks } from '../lib/musicTracks';
import './MusicPlayer.css';

const formatTime = (ms) => {
  const seconds = Math.floor((Number.isFinite(ms) ? ms : 0) / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

export function nextTrackIndex(current, direction, shuffled, count, random = Math.random) {
  if (count < 2) return 0;
  if (shuffled && direction > 0) return (current + 1 + Math.floor(random() * (count - 1))) % count;
  return (current + direction + count) % count;
}

let scriptPromise;
function loadWidgetApi() {
  if (window.SC?.Widget) return Promise.resolve(window.SC);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://w.soundcloud.com/player/api.js';
      const timeout = window.setTimeout(() => {
        script.remove(); reject(new Error('Player unavailable'));
      }, 15000);
      script.onload = () => {
        clearTimeout(timeout);
        if (window.SC?.Widget) resolve(window.SC);
        else { script.remove(); reject(new Error('Player unavailable')); }
      };
      script.onerror = () => { clearTimeout(timeout); script.remove(); reject(new Error('Player unavailable')); };
      document.body.appendChild(script);
    }).catch(error => { scriptPromise = null; throw error; });
  }
  return scriptPromise;
}

const MusicPlayer = () => {
  const iframeRef = useRef(null);
  const widgetRef = useRef(null);
  const actions = useRef({});
  const autoplay = useRef(false);
  const history = useRef([]);
  const volumeRef = useRef(80);
  const lastVolume = useRef(80);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState('all');
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [sound, setSound] = useState(null);
  const [nativePlayer, setNativePlayer] = useState(false);
  const track = tracks[active];

  function selectTrack(index, remember = true) {
    if (remember && index !== active) history.current.push(active);
    widgetRef.current?.pause();
    autoplay.current = true;
    setActive(index);
    if (index === active) { widgetRef.current?.seekTo(0); widgetRef.current?.play(); }
  }
  function skip(direction) {
    if (direction < 0 && position > 3000) { widgetRef.current?.seekTo(0); setPosition(0); return; }
    const previous = direction < 0 ? history.current.pop() : undefined;
    selectTrack(previous ?? nextTrackIndex(active, direction, shuffle, tracks.length), direction > 0);
  }
  function togglePlay() {
    if (!ready) return;
    if (playing) widgetRef.current?.pause();
    else widgetRef.current?.play();
  }
  function changeVolume(value) {
    setVolume(value);
    volumeRef.current = value;
    if (value > 0) lastVolume.current = value;
    widgetRef.current?.setVolume(value);
  }
  actions.current = { skip, repeat, active, shuffle };

  useEffect(() => {
    let cancelled = false;
    let widget;
    let timeout;
    setReady(false); setPlaying(false); setPosition(0); setDuration(0); setSound(null); setError('');
    const fail = () => {
      if (!cancelled) { setError('This track couldn’t load. Retry or listen on SoundCloud.'); setReady(false); setPlaying(false); }
    };
    timeout = window.setTimeout(fail, 18000);
    loadWidgetApi().then(SC => {
      if (cancelled) return;
      widget = SC.Widget(iframeRef.current);
      widgetRef.current = widget;
      const guarded = fn => (...args) => { if (!cancelled) fn(...args); };
      const metadata = () => {
        widget.getDuration(guarded(setDuration));
        widget.getCurrentSound(guarded(value => setSound(value)));
      };
      widget.bind(SC.Widget.Events.READY, guarded(() => {
        clearTimeout(timeout); setReady(true); setError('');
        widget.setVolume(volumeRef.current); metadata();
        if (autoplay.current) { widget.play(); autoplay.current = false; }
      }));
      widget.bind(SC.Widget.Events.PLAY, guarded(() => { setPlaying(true); setError(''); metadata(); }));
      widget.bind(SC.Widget.Events.PAUSE, guarded(() => setPlaying(false)));
      widget.bind(SC.Widget.Events.PLAY_PROGRESS, guarded(event => setPosition(event.currentPosition)));
      widget.bind(SC.Widget.Events.SEEK, guarded(event => setPosition(event.currentPosition)));
      widget.bind(SC.Widget.Events.ERROR, guarded(fail));
      widget.bind(SC.Widget.Events.FINISH, guarded(() => {
        setPlaying(false);
        const mode = actions.current.repeat;
        if (mode === 'one') { widget.seekTo(0); widget.play(); return; }
        // A queue entry may itself be a SoundCloud playlist. Finish its sounds first.
        widget.getSounds(guarded(sounds => widget.getCurrentSoundIndex(guarded(index => {
          if (index < sounds.length - 1) { widget.next(); return; }
          if (mode === 'off' && actions.current.active === tracks.length - 1 && !actions.current.shuffle) return;
          actions.current.skip(1);
        }))));
      }));
    }).catch(fail);
    return () => {
      cancelled = true; clearTimeout(timeout);
      if (widget) {
        // React may detach the iframe before passive-effect cleanup runs.
        // SoundCloud then throws while posting to the old contentWindow.
        try { widget.pause(); } catch { /* The detached frame has already stopped. */ }
        Object.values(window.SC.Widget.Events).forEach(event => {
          try { widget.unbind(event); } catch { /* Ignore detached-frame teardown. */ }
        });
      }
      widgetRef.current = null;
    };
  }, [active, retry]);

  const seek = value => { setPosition(value); widgetRef.current?.seekTo(value); };
  const soundTitle = sound?.title || track.title;
  const soundUrl = sound?.permalink_url || track.soundcloudUrl;
  const cycleRepeat = () => setRepeat(repeat === 'all' ? 'one' : repeat === 'one' ? 'off' : 'all');
  const progress = duration ? position / duration * 100 : 0;

  return (
    <section id="music" className="music-section" data-testid="music-section">
      <div className="section-container">
        <div className="music-header">
          <div><span className="section-label">THE K BEATS COLLECTION</span><h2 className="music-title">Find your <span>frequency.</span></h2></div>
          <p className="music-intro">Original sounds. No two alike.<br />Put your headphones on. Stay a while.</p>
        </div>
        <div data-testid="studio-player" className={`studio-player ${playing ? 'is-playing' : ''}`}>
          <div className="studio-main">
            <div className="studio-art" style={{ '--art-hue': `${active * 38}deg` }}>
              {sound?.artwork_url && <img src={sound.artwork_url} alt="" onError={e => { e.currentTarget.style.display = 'none'; }} />}
              <div className="art-topline"><span>K / BEATS</span><span>VOL. {String(active + 1).padStart(2, '0')}</span></div>
              <Disc3 className="art-disc" strokeWidth={0.6} aria-hidden="true" />
              <div className="art-caption"><span>ORIGINAL SOUND</span><strong>{track.title}</strong><span>K BEATS STUDIO</span></div>
            </div>
            <div className="now-playing">
              <div className="playing-label"><span className="equalizer" aria-hidden="true"><i /><i /><i /><i /></span>{playing ? 'NOW PLAYING' : ready ? 'READY WHEN YOU ARE' : error ? 'PLAYBACK UNAVAILABLE' : 'CONNECTING'}</div>
              <h3>{soundTitle}</h3>
              <a className="artist-credit" href={sound?.user?.permalink_url || 'https://soundcloud.com/k-beats-291187445'} target="_blank" rel="noreferrer">{sound?.user?.username || 'K Beats'}</a>
              <div className="music-progress">
                <input type="range" aria-label="Track progress" aria-valuetext={`${formatTime(position)} of ${formatTime(duration)}`} min="0" max={duration || 1} step="1000" value={Math.min(position, duration || 1)} disabled={!ready || !duration} onChange={e => seek(Number(e.target.value))} style={{ '--progress': `${progress}%` }} />
                <div><span>{formatTime(position)}</span><span>{formatTime(duration)}</span></div>
              </div>
              <div className="transport">
                <button type="button" aria-label="Shuffle" aria-pressed={shuffle} onClick={() => { setShuffle(!shuffle); history.current = []; }}><Shuffle size={19} /></button>
                <button type="button" aria-label="Previous track" onClick={() => skip(-1)}><SkipBack size={24} fill="currentColor" /></button>
                <button type="button" className="main-play" data-testid="player-play-pause" aria-label={playing ? 'Pause track' : 'Play track'} disabled={!ready} onClick={togglePlay}>{!ready && !error ? <Loader2 className="player-loading" size={26} /> : playing ? <Pause fill="currentColor" size={26} /> : <Play fill="currentColor" size={26} />}</button>
                <button type="button" aria-label="Next track" onClick={() => skip(1)}><SkipForward size={24} fill="currentColor" /></button>
                <button type="button" aria-label={`Repeat: ${repeat}`} aria-pressed={repeat !== 'off'} onClick={cycleRepeat}>{repeat === 'one' ? <Repeat1 size={19} /> : <Repeat size={19} />}</button>
              </div>
              <div className="player-bottom">
                <div className="music-volume"><button type="button" aria-label={volume ? 'Mute' : 'Unmute'} onClick={() => changeVolume(volume ? 0 : lastVolume.current)}>{volume ? <Volume2 size={18} /> : <VolumeX size={18} />}</button><input aria-label="Volume" type="range" min="0" max="100" value={volume} onChange={e => changeVolume(Number(e.target.value))} /></div>
                <a className="soundcloud-credit" href={soundUrl} target="_blank" rel="noreferrer">SoundCloud <ExternalLink size={13} /></a>
              </div>
              {error && <div className="player-error" role="alert">{error}<button type="button" onClick={() => setRetry(retry + 1)}><RotateCcw size={14} /> Retry</button></div>}
            </div>
          </div>
          <div className="studio-queue">
            <div className="queue-heading"><span><ListMusic size={19} /> Studio selections</span><small>{tracks.length} releases</small></div>
            <div className="music-track-list" aria-label="Track list">
              {tracks.map((item, index) => <button type="button" key={item.title} className={`music-track ${index === active ? 'is-active' : ''}`} aria-label={`${index === active && playing ? 'Pause' : 'Play'} ${item.title}`} aria-current={index === active ? 'true' : undefined} onClick={() => index === active ? togglePlay() : selectTrack(index)}>
                <span className="music-track-number">{index === active && playing ? <span className="equalizer" aria-hidden="true"><i /><i /><i /></span> : String(index + 1).padStart(2, '0')}</span>
                <span className="track-thumb" style={{ '--thumb-hue': `${index * 38}deg` }}><Disc3 size={24} strokeWidth={1} /></span>
                <span className="music-track-copy"><strong>{item.title}</strong><small>{item.type}</small></span>
                <span className="music-track-action">{index === active && playing ? <Pause size={17} /> : <Play size={17} />}</span>
              </button>)}
            </div>
            <div className="queue-foot"><span>Made by K Beats. Streamed via SoundCloud.</span><button type="button" aria-expanded={nativePlayer} onClick={() => setNativePlayer(!nativePlayer)}>{nativePlayer ? 'Hide' : 'Open'} embedded player</button></div>
          </div>
          <div className={nativePlayer ? 'soundcloud-engine is-visible' : 'soundcloud-engine'} aria-hidden={!nativePlayer}>
            <iframe key={`${active}-${retry}`} ref={iframeRef} title="SoundCloud audio source" src={track.embedUrl} allow="autoplay" tabIndex={nativePlayer ? 0 : -1} width="100%" height="166" />
          </div>
        </div>
      </div>
    </section>
  );
};
export default MusicPlayer;
